import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Sparkles } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import aiAssistApi, { type AiAssistParseResult } from '@/services/ai-assist'

type JobCreateFromStatementCardProps = {
  serviceInstanceId?: string | null
  disabled?: boolean
  onApplyDraft: (result: AiAssistParseResult) => void
}

export function JobCreateFromStatementCard({
  serviceInstanceId,
  disabled = false,
  onApplyDraft,
}: JobCreateFromStatementCardProps) {
  const [statement, setStatement] = useState('')
  const [result, setResult] = useState<AiAssistParseResult | null>(null)

  const parseMutation = useMutation({
    mutationFn: () =>
      aiAssistApi.parseCreate({
        statement,
        intentHint: 'job',
        serviceInstanceId,
      }),
    onSuccess: (parsed) => {
      setResult(parsed)
      if (parsed.intent === 'booking') {
        toast.message('That sounds like a booking. Use POS / Bookings to create it from a statement.')
        return
      }
      if (parsed.intent === 'unknown' && parsed.notes) {
        toast.message(parsed.notes)
        return
      }
      if (parsed.ready) {
        toast.success('Draft ready — review and apply to the form.')
      } else {
        toast.message('Draft needs a few more details. Review missing fields below.')
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error) || 'Could not parse statement.')
    },
  })

  const canParse = Boolean(statement.trim()) && Boolean(serviceInstanceId) && !disabled
  const fieldErrorEntries = Object.entries(result?.field_errors ?? {})

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4 text-sky-600" />
          Create from statement
        </CardTitle>
        <CardDescription>
          Describe the job in plain language. We will draft the form for you to review before saving.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="job-ai-statement">Statement</Label>
          <Textarea
            id="job-ai-statement"
            rows={3}
            value={statement}
            onChange={(event) => setStatement(event.target.value)}
            placeholder='e.g. "Create a high-priority install job at 10 High St for Acme on Friday 9am"'
            disabled={disabled || parseMutation.isPending}
          />
          {!serviceInstanceId ? (
            <p className="text-sm text-muted-foreground">Select a workspace before using AI assist.</p>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            onClick={() => parseMutation.mutate()}
            disabled={!canParse || parseMutation.isPending}
          >
            {parseMutation.isPending ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Parsing…
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Parse statement
              </>
            )}
          </Button>
          {result ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onApplyDraft(result)
                toast.success('Draft applied to the form.')
              }}
              disabled={disabled || result.intent !== 'job'}
            >
              Apply to form
            </Button>
          ) : null}
        </div>

        {result ? (
          <div className="space-y-3 rounded-lg border bg-muted/30 p-3 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{result.intent}</Badge>
              {result.intent === 'query' ? (
                <Badge variant="secondary">Answer</Badge>
              ) : (
                <Badge variant={result.ready ? 'default' : 'secondary'}>
                  {result.ready ? 'Ready' : 'Needs details'}
                </Badge>
              )}
            </div>

            {result.intent === 'query' && result.draft?.answer_text ? (
              <p className="text-foreground">{String(result.draft.answer_text)}</p>
            ) : null}

            {result.notes ? (
              <p className="text-muted-foreground">{result.notes}</p>
            ) : null}

            {result.missing_fields.length > 0 ? (
              <div>
                <p className="mb-1 font-medium text-foreground">Missing</p>
                <ul className="list-inside list-disc text-muted-foreground">
                  {result.missing_fields.map((field) => (
                    <li key={field}>{field}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            {fieldErrorEntries.length > 0 ? (
              <div>
                <p className="mb-1 font-medium text-destructive">Validation</p>
                <ul className="list-inside list-disc text-destructive">
                  {fieldErrorEntries.map(([field, messages]) => (
                    <li key={field}>
                      {field}: {messages.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {result.draft?.title ? (
              <div className="text-muted-foreground">
                <span className="font-medium text-foreground">Draft title:</span> {String(result.draft.title)}
              </div>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
